import { ArrowDropDown, InfoOutlined } from "@mui/icons-material";
import { Menu, MenuItem, Typography, Box, Divider, Stack } from "@mui/material";
import * as numbers from "common/numbers";
import Button from "components/common/Button";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as endorsementsStore from "store/endorsements";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

const CopyFromLayerButton = ({ layerId, type }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useDispatch();

  const layers = useSelector(pricingSelectors.getTower);
  const allEndorsementsData = useSelector(endorsementsStore.selectInput);

  if (!layerId || !layers) return null;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopy = (sourceLayerId) => {
    if (type === "ENDORSEMENTS") {
      const sourceEndorsements =
        allEndorsementsData?.endorsementsByLayer?.[sourceLayerId] ?? [];
      const targetEndorsements =
        allEndorsementsData?.endorsementsByLayer?.[layerId] ?? [];

      const existingTitles = new Set(targetEndorsements.map((e) => e.title));

      const newEndorsements = sourceEndorsements
        .filter((e) => !existingTitles.has(e.title))
        .map((e) => ({ ...e }));

      if (newEndorsements.length > 0) {
        dispatch(
          endorsementsStore.setLayerEndorsements({
            layerId,
            endorsements: [...targetEndorsements, ...newEndorsements],
          })
        );
      }
    } else if (type === "SUBJECTIVITIES") {
      const sourceSubjectivities =
        allEndorsementsData?.subjectivitiesByLayer?.[sourceLayerId] ?? [];
      const targetSubjectivities =
        allEndorsementsData?.subjectivitiesByLayer?.[layerId] ?? [];

      const existingKeys = new Set(targetSubjectivities.map((s) => s.key));

      const newSubjectivities = sourceSubjectivities
        .filter((s) => !existingKeys.has(s.key))
        .map((s) => ({ ...s }));

      if (newSubjectivities.length > 0) {
        dispatch(
          endorsementsStore.setLayerSubjectivities({
            layerId,
            subjectivities: [...targetSubjectivities, ...newSubjectivities],
          })
        );
      }
    }
    handleClose();
  };

  const otherLayers = layers.filter((l) => l.id !== layerId);

  if (otherLayers.length === 0) return null;

  const getCount = (lId) => {
    if (type === "ENDORSEMENTS") {
      return (allEndorsementsData?.endorsementsByLayer?.[lId] ?? []).length;
    } else {
      return (allEndorsementsData?.subjectivitiesByLayer?.[lId] ?? []).length;
    }
  };

  const label = type === "ENDORSEMENTS" ? "Endorsements" : "Subjectivities";
  const buttonLabel = `Copy ${label}`;

  return (
    <>
      <Button
        variant={"contained"}
        disableElevation
        endIcon={<ArrowDropDown />}
        onClick={handleClick}
        size={"small"}
        color={"primary"}
        sx={{ textTransform: "none" }}
      >
        {buttonLabel}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: { width: 300, maxHeight: 400 },
          },
        }}
      >
        {otherLayers.map((layer) => {
          const count = getCount(layer.id);
          const name = `${numbers.abbreviated(
            layer.limit
          )}x${numbers.abbreviated(layer.attachment)}`;

          return (
            <MenuItem key={layer.id} onClick={() => handleCopy(layer.id)}>
              <Box
                sx={{ display: "flex", flexDirection: "column", width: "100%" }}
              >
                <Typography variant={"body1"} sx={{ fontWeight: 500 }}>
                  {name}
                </Typography>
                <Typography variant={"caption"} color={"text.secondary"}>
                  {count} {label.toLowerCase()}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
        <Divider />
        <Box sx={{ p: 1.5 }}>
          <Stack direction={"row"} spacing={1} alignItems={"flex-start"}>
            <InfoOutlined
              fontSize={"small"}
              color={"action"}
              sx={{ fontSize: 16, mt: 0.3 }}
            />
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ fontStyle: "italic", lineHeight: 1.3 }}
            >
              {"Existing "}
              {label.toLowerCase()}
              {" on this layer will be unchanged, duplicate "}
              {label.toLowerCase()}
              {" will be ignored"}
            </Typography>
          </Stack>
        </Box>
      </Menu>
    </>
  );
};

export default CopyFromLayerButton;
